import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, Mail, ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// ============= NAV =============
// Early-access site. 3 items: Grow (U/A), Learn (No Pressure), Build.
const NAV = [
  { to: "/unfair-advantage", label: "Grow",  testid: "nav-grow"  },
  { to: "/no-pressure",      label: "Learn", testid: "nav-learn" },
  { to: "/build",            label: "Build", testid: "nav-build" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setIsAdmin(false); setUnread(0); return; }
    api.get("/auth/admin-check").then(({ data }) => setIsAdmin(!!data.is_admin)).catch(() => setIsAdmin(false));
    api.get("/inbox/unread").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    const t = setInterval(() => api.get("/inbox/unread").then(({ data }) => setUnread(data.unread || 0)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [user]);

  // close mobile drawer on navigation
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 shrink-0 -my-2"
        >
          <img src="/anartizen-logo.png" alt="anArtizen" className="h-24 md:h-28 w-auto select-none" draggable={false} />
        </Link>

        {/* MD+ nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {NAV.map((n) =>
            n.items ? (
              <NavDropdown key={n.label} item={n} location={location} />
            ) : (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`font-mono text-[11px] lg:text-[12px] uppercase tracking-[0.2em] link-underline whitespace-nowrap ${
                  location.pathname.startsWith(n.to) ? "text-primary" : "text-white/85 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin" className="btn-pill hidden md:inline-flex">
              <ShieldCheck size={13} /> Admin
            </Link>
          )}
          {user ? (
            <>
              <Link to="/inbox" data-testid="nav-inbox" className="relative p-2 hover:text-primary" aria-label="Inbox">
                <Mail size={17} />
                {unread > 0 && <span data-testid="nav-inbox-dot" className="absolute top-1 right-1 block w-2 h-2 bg-primary rounded-full" />}
              </Link>
              <AvatarMenu user={user} logout={logout} />
            </>
          ) : null}
          <button className="md:hidden btn-pill !p-2" onClick={() => setOpen(!open)} aria-label="menu" data-testid="nav-mobile-toggle">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {/* MOBILE drawer */}
      {open && (
        <div className="md:hidden border-t border-border bg-background" data-testid="nav-mobile-menu">
          <div className="px-5 py-5 grid gap-5">
            {NAV.map((n) =>
              n.items ? (
                <div key={n.label} className="grid gap-2">
                  <p className="meta-mono !text-white/45">— {n.label.toUpperCase()}</p>
                  {n.items.map((it) => (
                    <Link key={it.to} to={it.to} data-testid={`m-${it.testid}`} className={`font-mono text-xs tracking-[0.18em] uppercase ${location.pathname === it.to ? "text-primary" : "text-white/85"}`}>{it.label}</Link>
                  ))}
                </div>
              ) : (
                <Link key={n.to} to={n.to} data-testid={`m-${n.testid}`} className={`font-mono text-xs tracking-[0.18em] uppercase ${location.pathname.startsWith(n.to) ? "text-primary" : "text-white/85"}`}>{n.label}</Link>
              )
            )}
            {user && (
              <Link to="/inbox" data-testid="m-nav-inbox" className="border-t border-border pt-4 font-mono text-xs tracking-[0.18em] uppercase flex items-center gap-2">
                <Mail size={14} /> Inbox {unread > 0 && <span className="text-primary">· {unread} unread</span>}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function NavDropdown({ item, location }) {
  const [open, setOpen] = useState(false);
  const isActive = item.items.some((d) => location.pathname.startsWith(d.to));
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={item.testid}
        onClick={() => setOpen(!open)}
        className={`font-mono text-[11px] lg:text-[12px] uppercase tracking-[0.2em] link-underline flex items-center gap-1 whitespace-nowrap ${isActive ? "text-primary" : "text-white/85 hover:text-white"}`}
      >
        {item.label}
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 min-w-[240px]">
          <div className="border border-border bg-background shadow-2xl">
            {item.items.map((d) => (
              <Link
                key={d.to}
                to={d.to}
                data-testid={d.testid}
                className={`block p-4 hover:bg-white/[0.04] border-b border-border last:border-b-0 ${location.pathname.startsWith(d.to) ? "bg-white/[0.02]" : ""}`}
              >
                <p className="font-display text-base font-bold leading-tight">{d.label}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AvatarMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);
  const tierPill = user.tier_label || (user.tier ? `The ${user.tier}` : "Artizen");
  return (
    <div className="relative" ref={ref}>
      <button data-testid="nav-avatar" onClick={() => setOpen(!open)} className="flex items-center gap-2 btn-pill !pl-1.5">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center text-[10px] font-mono">{(user.name || "?")[0]}</span>
        )}
        <span className="hidden sm:inline">{user.name?.split(" ")[0] || "Me"}</span>
        <span className="hidden lg:inline meta-mono !text-primary/80">· {tierPill}</span>
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 min-w-[220px] border border-border bg-background shadow-2xl z-50">
          {[
            { to: "/dashboard",                       label: "Dashboard",          testid: "menu-dashboard" },
            { to: "/dashboard?tab=profile",           label: "My Profile",         testid: "menu-profile" },
            { to: "/inbox",                           label: "Inbox",              testid: "menu-inbox" },
            { to: "/membership",                      label: "Membership",         testid: "menu-membership" },
            { to: "/dashboard?tab=settings",          label: "Settings",           testid: "menu-settings" },
          ].map((it) => (
            <Link key={it.to} to={it.to} data-testid={it.testid} onClick={() => setOpen(false)} className="block px-4 py-3 hover:bg-white/[0.04] border-b border-border font-mono text-[11px] tracking-[0.18em] uppercase">{it.label}</Link>
          ))}
          <button onClick={() => { setOpen(false); logout(); }} data-testid="menu-logout" className="w-full text-left px-4 py-3 hover:bg-white/[0.04] font-mono text-[11px] tracking-[0.18em] uppercase text-primary flex items-center gap-2">
            <LogOut size={12} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

// ============= FOOTER =============
export function Footer() {
  return (
    <footer className="border-t border-border mt-24">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="block w-2.5 h-2.5 bg-primary rounded-full" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase">anArtizen</span>
          </div>
          <p className="font-display text-3xl md:text-4xl leading-[0.95] tracking-tight">
            A home for <span className="hl-cyan">India's creatives</span> <br /> — and the people who hire them.
          </p>
          <p className="meta-mono mt-6">© 2026 · anArtizen</p>
        </div>
        <FooterCol title="Early Access" items={[
          ["Grow · Unfair Advantage", "/unfair-advantage"],
          ["Learn · No Pressure",     "/no-pressure"],
          ["Build",                   "/build"],
        ]} />
        <FooterCol title="anArtizen" items={[
          ["Our Story",     "/about"],
          ["The Manifesto", "/manifesto"],
          ["Contact",       "/contact"],
        ]} />
        <FooterCol title="Legal" items={[
          ["Privacy",       "/privacy"],
          ["Terms",         "/terms"],
          ["Refunds",       "/refunds"],
        ]} />
      </div>
      <div className="border-t border-border px-5 md:px-10 py-5 flex flex-wrap items-center justify-between gap-3 max-w-[1600px] mx-auto">
        <span className="meta-mono">Built in India · for India · with rigour</span>
        <span className="meta-mono">MUMBAI · DELHI · BANGALORE · CHENNAI</span>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <p className="meta-mono mb-4 !text-primary">— {title}</p>
      <ul className="space-y-2.5">
        {items.map(([label, to]) => (
          <li key={label}><Link to={to} className="text-sm text-white/85 hover:text-white link-underline">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

export function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export function SlideCorner({ left = "ANARTIZEN", right = "" }) {
  // Corner markers (red-dot ANARTIZEN + right-side section tag) removed
  // sitewide per design request. Kept as a no-op so all page-level usages
  // continue to compile without change — restore by reinstating the JSX below.
  void left; void right;
  return null;
}

export function SectionLabel({ children }) {
  return <p className="label-mono mb-6">{children}</p>;
}
